import { Box, Grid, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

export interface SpecTileProps {
  icon: ReactNode;
  label: string;
  value: string;
  /** Highlight tile in primary brand color — use for the headline spec only. */
  accent?: boolean;
}

export function SpecTile({ icon, label, value, accent }: SpecTileProps) {
  return (
    <Box
      sx={{
        position: "relative",
        p: { xs: 2, md: 2.5 },
        borderRadius: 1,
        border: "1px solid",
        borderColor: accent ? "primary.main" : "divider",
        bgcolor: accent ? "rgba(255,176,0,0.08)" : "background.paper",
        height: "100%",
        overflow: "hidden",
        "&::before": accent
          ? {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at top right, rgba(255,176,0,0.18), transparent 60%)",
              pointerEvents: "none",
            }
          : undefined,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
        <Box sx={{ color: accent ? "primary.main" : "text.secondary", display: "flex" }}>
          {icon}
        </Box>
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1, fontSize: 11 }}>
          {label}
        </Typography>
      </Stack>
      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: 22, md: 28 },
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.1,
          color: accent ? "primary.main" : "text.primary",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

interface GridProps {
  tiles: SpecTileProps[];
  cols?: { xs?: number; sm?: number; md?: number };
}

/**
 * Responsive spec grid — defaults to 2/2/4 columns. Auto-skips on empty array.
 */
export function SpecTileGrid({ tiles, cols }: GridProps) {
  if (tiles.length === 0) return null;
  const xs = cols?.xs ?? 6;
  const sm = cols?.sm ?? 6;
  const md = cols?.md ?? Math.max(3, Math.floor(12 / Math.min(tiles.length, 4)));
  return (
    <Grid container spacing={{ xs: 1.5, md: 2 }}>
      {tiles.map((t, i) => (
        <Grid key={i} size={{ xs, sm, md }}>
          <SpecTile {...t} />
        </Grid>
      ))}
    </Grid>
  );
}
